import {
    CubeResult,
    CurrencyValue,
    FormCurrencyAmount,
    WdxDateFormat,
    WdxDateTimeService,
} from '@wdx/shared/utils';
import {
    Column,
    ICellRendererParams,
    ValueFormatterParams,
    ValueGetterFunc,
    ValueGetterParams,
    ValueSetterParams,
} from 'ag-grid-community';

/**
 * For use in AG Grid Colummns where a column can display both standard row cells
 * and also be used for grouping.
 * IMPORTANT: when getting group data ensure `__group_row__: true` is added to each group row
 * @param groupName name of group in cube response
 * @param groupProp
 * @returns
 */
export const DYNAMIC_GROUP_BY_VALUE_GETTER = (
    groupName: string,
    groupProp: string | null = 'name',
) =>
    ((params: ValueGetterParams<CubeResult & { __group_row__?: boolean }>) => {
        if (params.data?.__group_row__) {
            if (!params.column.isRowGroupActive()) {
                return;
            }
            return params.data?.groups?.[groupName]
                ? `${
                      groupProp
                          ? params.data?.groups?.[groupName]?.[groupProp] || ''
                          : params.data?.groups?.[groupName] || ''
                  } (${params.data?.rollupValue})`
                : ` (${params.data?.rollupValue})`;
        }
        return AG_GRID_VALUE_GETTER(params);
    }) as ValueGetterFunc;

/**
 * @deprecated This function has been deprecated use DATE_CELL_VALUE_FORMATTER instead
 */
export const DATE_STRING_VALUE_GETTER: ValueGetterFunc = (
    params: ValueGetterParams,
) => {
    const field = params.column.getColDef().field;
    const dateObj =
        field && params.data?.[field]
            ? new Date(params.data[field])
            : undefined;

    if (!dateObj) {
        return;
    }
    return dateObj.getTime() ? dateObj : undefined;
};

export const DATE_CELL_VALUE_GETTER = (
    dateTimeService: WdxDateTimeService,
    format = WdxDateFormat.AbsoluteDate,
) =>
    ((params: ValueGetterParams) => {
        const value = AG_GRID_VALUE_GETTER(params);
        return value
            ? dateTimeService.convertDateToViewFriendlyFormat(value, { format })
            : '';
    }) as ValueGetterFunc;

export const CURRENCY_CELL_VALUE_GETTER: ValueGetterFunc = (
    params: ValueGetterParams<any, FormCurrencyAmount>,
) => {
    const value = AG_GRID_VALUE_GETTER(params);
    return value?.amount;
};

/**
 * Not a true value getter i.e. not ValueGetterFunc, this is convenience function to get the
 * field value for various use cases
 */
export const AG_GRID_VALUE_GETTER_BY_FIELD = (
    params:
        | ValueFormatterParams
        | ValueGetterParams
        | ICellRendererParams
        | ValueSetterParams,
    fieldName?: string,
) => {
    const column = params.column as Column;
    const field = fieldName || column?.getColDef()?.field || column?.getColId();
    return field
        ? _getValueUsingField(
              params.data,
              field,
              Boolean(field?.includes('.')),
          ) || ''
        : '';
};

export const AG_GRID_VALUE_GETTER: ValueGetterFunc = (
    params: ValueGetterParams,
) => {
    const column = params.column as Column;
    const field = column?.getColDef()?.field || column?.getColId();
    return field
        ? _getValueUsingField(
              params.data,
              field,
              Boolean(field?.includes('.')),
          ) || ''
        : '';
};

// Taken from https://github.com/ag-grid/ag-grid as no longer exposed by the api
function _getValueUsingField(
    data: any,
    field: string,
    fieldContainsDots: boolean,
): any {
    if (!field || !data) {
        return;
    }

    // if no '.', then it's not a deep value
    if (!fieldContainsDots) {
        return data[field];
    }

    // otherwise it is a deep value, so need to dig for it
    const fields = field.split('.');
    let currentObject = data;

    for (let i = 0; i < fields.length; i++) {
        if (currentObject == null) {
            return undefined;
        }
        currentObject = currentObject[fields[i]];
    }

    return currentObject;
}

/**
 * Converts a value or object field to a readable string.
 * Works with strings, numbers, or objects containing `code`, `name`, or `value`.
 */
export function getStringValue(rowOrValue: any, fieldName?: string): string {
    const value = fieldName ? rowOrValue?.[fieldName] : rowOrValue;

    if (value == null) {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }
    if (typeof value === 'object') {
        return String(
            value.code ??
                value.name ??
                value.value ??
                Object.values(value)
                    .map((v) => getStringValue(v))
                    .find((v) => v) ??
                '',
        );
    }

    return String(value);
}
